import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ReactTimeout from 'react-timeout';
import _ from 'lodash';
import NetworkImage from 'components/NetworkImage';
import { helperGetHomeUrl } from 'helpers/RouteHelper';
import { callTealiumPageView, PageNames } from '@foxcorp/delta-web-analytics';
import { determineBrowser } from 'components/Header/HeaderHelpers';
import Loader from 'components/Loader';
import helperMarkdownToComponent from 'helpers/Data';
import { tveEnabled } from 'helpers/ConfigHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
} from 'helpers/BreakpointHelper';
import { isStaticRender } from 'helpers/ContentHelper';
import Style from './SignIn.module.scss';

const {
  legal_disclaimer: legalDisclaimer,
  legal_expirationHeader: legalExpirationHeader,
  legal_expirationText: legalExpirationText,
  auth_enterCodeButton: enterCodeButton,
  auth_enterCodeButtonHint: enterCodeButtonHint,
} = window.Language;

const PROVIDER_ID = 'Fox_MVPD';
const blurTimeout = 1500;
const idpLaunchDelay = 500;

const desktopBackground = _.get(window.ExternalConfig, 'singleSignIn.header.desktop', null);
const mobileBackground = _.get(window.ExternalConfig, 'singleSignIn.header.mobile', null);

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    backgroundImage: desktopBackground,
    logoHeight: 80,
  },
  [SITE_PLATFORM_WEB]: {
    backgroundImage: desktopBackground,
    logoHeight: 80,
  },
  [SITE_PLATFORM_TABLET]: {
    backgroundImage: desktopBackground,
    logoHeight: 80,
  },
  [SITE_PLATFORM_MOBILE]: {
    backgroundImage: mobileBackground,
    logoHeight: 50,
  },
};

class SignIn extends Component {
  constructor(props) {
    super(props);
    this.state = {
      renderCTA: true,
      isAuthenticating: false,
      idpIsLoading: true,
      showCookiesBlocked: false,
      singleProviderSignInClicked: false,
    };
    window.addEventListener('focus', () => this.focusSignIn());
    window.aeErrorHandling = this.accessEnablerAdvancedErrorHandler;
  }

  componentDidMount() {
    const { deviceId, fetchMVPDProviders } = this.props;
    const shouldFetchMVPDProviders = tveEnabled();
    if (shouldFetchMVPDProviders) fetchMVPDProviders(deviceId);
    this.pageViewEvent();
  }

  componentDidUpdate(prevProps) {
    const { authSuccess, mvpdProviders } = this.props;
    const { authSuccess: prevAuthSuccess } = prevProps;
    const { idpIsLoading } = this.state;

    if (idpIsLoading && !authSuccess) {
      const mvpd = _.find(
        mvpdProviders,
        provider => _.get(provider, 'adobePassId') === PROVIDER_ID,
      );
      if (mvpd) this.setState({ idpIsLoading: false });
    }

    if (authSuccess && !prevAuthSuccess) window.location.href = helperGetHomeUrl();
  }

  accessEnablerAdvancedErrorHandler = err => {
    const errorId = _.get(err, 'errorId');
    const browser = determineBrowser();
    const isSafari = browser === 'safari';
    const cookiesDisabled = errorId === 'N130';
    const shouldShowCookiesBlocked = cookiesDisabled && isSafari;
    if (shouldShowCookiesBlocked) {
      this.setState({ showCookiesBlocked: true });
    }
  };

  clickLoginAction = async () => {
    const { setTimeout } = this.props;
    this.setState({ isAuthenticating: true });
    setTimeout(() => {
      this.setState({ singleProviderSignInClicked: true });
    }, idpLaunchDelay);
  };

  resetShowCookiesBlocked = () => {
    this.setState({ showCookiesBlocked: false });
  };

  resetSingleProviderSignInClicked = () => {
    this.setState({ singleProviderSignInClicked: false });
  };

  pageViewEvent = async () => {
    if (!isStaticRender()) {
      const { getUseUtagGlobal } = this.props;
      await callTealiumPageView({
        pageName: PageNames.SIGN_IN,
        useUtagGlobal: getUseUtagGlobal(),
      });
    }
  };

  focusSignIn() {
    const { setTimeout } = this.props;
    const { isAuthenticating } = this.state;
    if (isAuthenticating) {
      setTimeout(() => {
        this.setState({ isAuthenticating: false });
      }, blurTimeout);
    }
  }

  renderCTA() {
    const { idpIsLoading } = this.state;
    if (idpIsLoading) {
      return <Loader className={Style.loader} />;
    }
    return (
      <div className={Style.signInCTA}>
        <div>
          <button
            className={Style.actionButton}
            onClick={() => {
              this.clickLoginAction();
            }}
            type="button"
          >
            {enterCodeButton}
          </button>
        </div>
        <small>{enterCodeButtonHint}</small>
      </div>
    );
  }

  renderErrorMessage() {
    const { showCookiesBlocked } = this.state;
    const {
      playback_cookieErrorHeader: headerText,
      playback_cookieErrorMessage: descriptionText,
    } = window.Language;
    if (showCookiesBlocked) {
      return (
        <div className={Style.error}>
          <span className={Style.errorTitle}>{headerText}</span>
          <span className={Style.errorDescription}>
            {helperMarkdownToComponent(descriptionText)}
          </span>
        </div>
      );
    }
    return null;
  }

  render() {
    const { breakpoint, renderAuthContainer } = this.props;
    const {
      isAuthenticating,
      renderCTA,
      showCookiesBlocked,
      singleProviderSignInClicked,
    } = this.state;
    const { backgroundImage, logoHeight } = BREAKPOINTS[breakpoint];
    const containerClasses = classnames(
      Style.signInContainer,
      renderCTA ? '' : Style.postDisclaimer,
    );
    const blurClasses = classnames(isAuthenticating ? Style.blurred : '');
    const errorMessage = this.renderErrorMessage();
    const authProps = {
      singleProviderId: PROVIDER_ID,
      singleProviderSignInClicked,
      resetShowCookiesBlocked: this.resetShowCookiesBlocked,
      resetSingleProviderSignInClicked: this.resetSingleProviderSignInClicked,
      showCookiesBlocked,
    };
    return (
      <div className={containerClasses}>
        <div className={Style.signInHeader}>
          <NetworkImage size={logoHeight} extended />
        </div>
        <div className={blurClasses}>
          <div className={Style.signInMedia}>
            <img src={backgroundImage} alt="For your consideration" />
          </div>
          {errorMessage}
          <div className={Style.signInUX}>
            {this.renderCTA()}
            {renderAuthContainer(authProps)}
          </div>
          <div className={Style.signInFixedFooter}>
            <div className={Style.signInFooterExpiration}>
              <p className={Style.signInFooterExpirationHeader}>{legalExpirationHeader}</p>
              <p className={Style.signInFooterExpirationText}>{legalExpirationText}</p>
            </div>
            <div className={Style.signInFooterDisclaimer}>
              <hr className={classnames(Style.divider)} />
              <p>{legalDisclaimer}</p>
              <hr className={classnames(Style.divider, Style.bottomDivider)} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

SignIn.defaultProps = {
  authSuccess: false,
  deviceId: null,
};

SignIn.propTypes = {
  authSuccess: PropTypes.bool,
  breakpoint: PropTypes.string.isRequired,
  deviceId: PropTypes.string,
  getUseUtagGlobal: PropTypes.func.isRequired,
  fetchMVPDProviders: PropTypes.func.isRequired,
  mvpdProviders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  renderAuthContainer: PropTypes.func.isRequired,
  setTimeout: PropTypes.func.isRequired,
};

const TimeoutSignIn = ReactTimeout(SignIn);

export default TimeoutSignIn;
